import React, { useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { toDateString } from '../../../utils/date';
import RevenueEventModal from './RevenueEventModal';
import { buildCurrentRevenueContext } from './RevenueEventModal.currentContext';
import {
  mobileActionRow,
  mobileAddButton,
  mobileAmount,
  mobileCard,
  mobileCardHeader,
  mobileCardList,
  mobileCardTitle,
  mobileEditButton,
  mobileEmpty,
  mobileInfoGrid,
  mobileInfoItem,
  mobileInfoLabel,
  mobileInfoValue,
  mobileSubText,
  mobileTitleText,
} from './ReservationDetail.mobileStyles';

const EVENT_LABELS = {
  booking: '예약',
  change: '변경',
  cancel: '취소',
  adjustment: '조정',
};

const SERVICE_TYPES = ['flight', 'car', 'hotel'];
const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const fmt = (value) => num(value).toLocaleString('ko-KR');
const date = (value) => (value ? toDateString(value) : '-');

function hasValue(value) {
  return value !== null && value !== undefined && value !== '';
}

function effectiveRefund(item) {
  if (hasValue(item.supplier_refund)) return num(item.supplier_refund);
  return hasValue(item.supplier_cancel_fee) && num(item.target_cost) !== 0
    ? num(item.target_cost) - num(item.supplier_cancel_fee)
    : 0;
}

function effectiveCancelFee(item) {
  if (hasValue(item.supplier_cancel_fee)) return num(item.supplier_cancel_fee);
  return hasValue(item.supplier_refund) && num(item.target_cost) !== 0
    ? num(item.target_cost) - num(item.supplier_refund)
    : 0;
}

function summarize(row) {
  if (!row.items?.length) {
    return {
      customerDelta: row.customer_amount,
      serviceRefund: 0,
      serviceCancelFee: ['change', 'cancel'].includes(row.event_type) ? row.total_cost : 0,
      additionalCost: row.total_cost,
      misc: row.misc,
    };
  }

  const summary = { customerDelta: 0, serviceRefund: 0, serviceCancelFee: 0, additionalCost: 0, misc: 0 };
  for (const item of row.items) {
    if (item.item_type === 'customer') {
      summary.customerDelta += num(item.customer_additional_payment) - num(item.customer_refund);
    } else if (item.item_type === 'misc') {
      summary.misc += num(item.misc_amount);
    } else if (SERVICE_TYPES.includes(item.item_type)) {
      summary.serviceRefund += effectiveRefund(item);
      summary.serviceCancelFee += effectiveCancelFee(item);
      summary.additionalCost += num(item.additional_cost);
    }
  }
  return summary;
}

function summarizeRefunds(row) {
  const refunds = Array.isArray(row.refunds) ? row.refunds : [];
  const count = refunds.length;
  const total = refunds.reduce((sum, refund) => sum + num(refund.amount), 0);
  const missingCashReceiptCancel = refunds.some((refund) => (
    refund.refund_method === 'cash'
    && num(refund.amount) > 0
    && !String(refund.cash_receipt_cancel_approval_no || '').trim()
  ));
  if (count === 0) return { text: '-', missing: false };
  return {
    text: `${count}건 · ${fmt(total)}${missingCashReceiptCancel ? ' · 취소번호 확인' : ''}`,
    missing: missingCashReceiptCancel,
  };
}

export default function RevenueEventsPanel({ rows = [], travelCode, baseline = {}, flights = [], hotels = [], masterNotes = '', onChanged, isMobile = false }) {
  const [modalRow, setModalRow] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const total = useMemo(() => rows.reduce((sum, row) => sum + num(row.amount), 0), [rows]);
  const currentContext = useMemo(
    () => buildCurrentRevenueContext(rows, baseline, flights, hotels),
    [rows, baseline, flights, hotels]
  );
  const modalContext = modalRow ? { baseline, flights, hotels } : currentContext;

  async function remove(row) {
    if (!window.confirm('수익 이벤트를 삭제할까요?')) return;
    const res = await apiClient.delete(`/api/admin/reservation-revenue-events/${row.id}`);
    if (res.ok && res.data?.success) onChanged && onChanged();
    else alert(res.data?.message || '삭제 실패');
  }

  return (
    <section style={panelStyle(isMobile)}>
      <div style={headerStyle(isMobile)}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)' }}>수익 이벤트</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>예약, 변경, 취소, 조정을 반영일 기준으로 월별 집계합니다.</div>
        </div>
        <div style={summaryActionStyle(isMobile)}>
          <strong style={{ fontSize: 13, color: total < 0 ? '#dc2626' : 'var(--text)' }}>합계 {fmt(total)}</strong>
          <button onClick={() => { setModalRow(null); setModalOpen(true); }} style={isMobile ? mobileAddButton : addBtn}>+ 추가</button>
        </div>
      </div>

      {isMobile ? (
        <MobileRevenueEvents rows={rows} onEdit={(row) => { setModalRow(row); setModalOpen(true); }} onDelete={remove} />
      ) : (
      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <Th>반영일</Th>
              <Th>유형</Th>
              <Th right>고객변화</Th>
              <Th>환불내역</Th>
              <Th right>공급사환불</Th>
              <Th right>추가원가</Th>
              <Th right>기타</Th>
              <Th right>이전반영</Th>
              <Th right>이번달수익</Th>
              <Th>관리</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={10} style={emptyTd}>수익 이벤트가 없습니다.</td></tr>
            ) : rows.map((row) => {
              const editable = row.source === 'manual' && !row.is_locked;
              const summary = summarize(row);
              const refundInfo = summarizeRefunds(row);
              return (
                <tr key={row.id}>
                  <Td>{date(row.event_date)}</Td>
                  <Td>{EVENT_LABELS[row.event_type] || row.event_type}</Td>
                  <Td right danger={summary.customerDelta < 0}>{fmt(summary.customerDelta)}</Td>
                  <Td danger={refundInfo.missing}>{refundInfo.text}</Td>
                  <Td right>{fmt(summary.serviceRefund)}</Td>
                  <Td right danger={summary.additionalCost > 0}>{fmt(summary.additionalCost)}</Td>
                  <Td right>{fmt(summary.misc)}</Td>
                  <Td right>{fmt(row.previous_recognized)}</Td>
                  <Td right strong danger={num(row.amount) < 0}>{fmt(row.amount)}</Td>
                  <Td>
                    {editable ? (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button style={miniBtn} onClick={() => { setModalRow(row); setModalOpen(true); }}>수정</button>
                        <button style={miniBtn} onClick={() => remove(row)}>삭제</button>
                      </div>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{row.source === 'import' ? '가져온값' : '자동'}</span>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {modalOpen && (
        <RevenueEventModal
          travelCode={travelCode}
          row={modalRow}
          baseline={modalContext.baseline}
          sourceFlights={modalContext.flights}
          sourceHotels={modalContext.hotels}
          masterNotes={masterNotes}
          isMobile={isMobile}
          onClose={() => setModalOpen(false)}
          onSaved={() => onChanged && onChanged()}
        />
      )}
    </section>
  );
}

function MobileRevenueEvents({ rows, onEdit, onDelete }) {
  if (rows.length === 0) return <div style={mobileEmpty}>수익 이벤트가 없습니다.</div>;

  return (
    <div style={mobileCardList}>
      {rows.map((row) => {
        const editable = row.source === 'manual' && !row.is_locked;
        const summary = summarize(row);
        const refundInfo = summarizeRefunds(row);
        return (
          <div key={row.id} style={mobileCard}>
            <div style={mobileCardHeader}>
              <div style={mobileCardTitle}>
                <span style={mobileTitleText}>{date(row.event_date)} · {EVENT_LABELS[row.event_type] || row.event_type}</span>
                <span style={mobileSubText}>{sourceLabel(row)}</span>
              </div>
              <span style={mobileAmount(num(row.amount) < 0)}>{fmt(row.amount)}</span>
            </div>
            <div style={mobileInfoGrid}>
              <Info label="고객변화" value={fmt(summary.customerDelta)} danger={summary.customerDelta < 0} />
              <Info label="환불내역" value={refundInfo.text} danger={refundInfo.missing} />
              <Info label="공급사환불" value={fmt(summary.serviceRefund)} />
              <Info label="추가원가" value={fmt(summary.additionalCost)} danger={summary.additionalCost > 0} />
              <Info label="기타" value={fmt(summary.misc)} />
              <Info label="이전반영" value={fmt(row.previous_recognized)} />
              <Info label="이번달수익" value={fmt(row.amount)} danger={num(row.amount) < 0} strong />
            </div>
            <div style={mobileActionRow}>
              {editable ? (
                <>
                  <button style={mobileEditButton} onClick={() => onEdit(row)}>수정</button>
                  <button style={miniDangerBtn} onClick={() => onDelete(row)}>삭제</button>
                </>
              ) : (
                <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{sourceLabel(row)}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Info({ label, value, danger, strong }) {
  return (
    <div style={mobileInfoItem}>
      <span style={mobileInfoLabel}>{label}</span>
      <span style={{ ...mobileInfoValue, color: danger ? '#dc2626' : 'var(--text)', fontWeight: strong ? 800 : 500 }}>{value}</span>
    </div>
  );
}

function sourceLabel(row) {
  if (row.source === 'import') return '가져온값';
  if (row.source === 'manual') return row.is_locked ? '잠김' : '수동 입력';
  return '자동';
}

function Th({ children, right }) {
  return <th style={{ ...thStyle, textAlign: right ? 'right' : 'left' }}>{children}</th>;
}

function Td({ children, right, strong, danger }) {
  return (
    <td style={{
      ...tdStyle,
      textAlign: right ? 'right' : 'left',
      fontWeight: strong ? 800 : 500,
      color: danger ? '#dc2626' : 'var(--text)',
    }}>
      {children}
    </td>
  );
}

const panelStyle = (isMobile) => ({ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: isMobile ? 10 : 12, display: 'grid', gap: 10, minWidth: 0 });
const headerStyle = (isMobile) => ({ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 12, borderBottom: '1px solid var(--border)', paddingBottom: 8, flexWrap: 'wrap' });
const summaryActionStyle = (isMobile) => ({ display: 'flex', gap: 8, alignItems: 'center', justifyContent: isMobile ? 'space-between' : 'flex-end', width: isMobile ? '100%' : 'auto' });
const addBtn = { padding: '5px 12px', background: 'var(--accent-700, #7c3aed)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700 };
const miniBtn = { padding: '3px 7px', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', fontSize: 11 };
const miniDangerBtn = { ...miniBtn, color: '#dc2626', borderColor: '#dc2626' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', minWidth: 1040, fontSize: 12 };
const thStyle = { padding: '6px 8px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', whiteSpace: 'nowrap', fontWeight: 700 };
const tdStyle = { padding: '7px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
const emptyTd = { padding: 16, textAlign: 'center', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' };
